import { useState, useEffect } from 'react'
import {
  listAccounts,
  listBalanceQueries,
  queryBalance,
  createAccount,
  setAccountActive,
  updateAccountCredentials,
  type Account,
  type BalanceQuery,
  type AccountCredentials,
} from '../api/monitoring'
import { fmtMoney, titleCase } from '../lib/format'
import { StatusChip } from '../components/StatusChip'
import { DrawerShell } from '../components/Drawer'
import { Can } from '../rbac/Can'
import { useUI } from '../store/ui'

const COLS = 'minmax(180px,1fr) 120px 140px 110px 140px 150px 200px'

const ROLES = ['collection', 'disbursement', 'portfolio']

// latest successful balance per account number, from the balance-query history
function latestBalances(rows: BalanceQuery[]): Record<string, BalanceQuery> {
  const out: Record<string, BalanceQuery> = {}
  for (const r of rows) {
    if (r.status !== 'success') continue
    if (!out[r.account_number]) out[r.account_number] = r // rows arrive newest-first
  }
  return out
}

export function Accounts() {
  const [accounts, setAccounts] = useState<Account[] | null>(null)
  const [bals, setBals] = useState<Record<string, BalanceQuery>>({})
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [manage, setManage] = useState<Account | null>(null)
  const showToast = useUI((s) => s.showToast)

  async function load() {
    setErr('')
    try {
      const [accts, balPage] = await Promise.all([listAccounts(), listBalanceQueries(100, 0)])
      setAccounts(accts)
      setBals(latestBalances(balPage.items))
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load accounts')
    }
  }
  useEffect(() => {
    void load()
  }, [])

  async function runBalance(a: Account) {
    setBusy(a.id)
    try {
      await queryBalance({ account_id: a.id })
      showToast(`Balance query sent for ${a.name} — the result appears under Transactions → Balances`, 'success')
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Balance query failed', 'danger')
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Accounts</h1>
          <div className="desc">The M-Pesa shortcodes and bank accounts the console collects into and disburses from.</div>
        </div>
        <Can perm="manage:config">
          <button className="btn primary" onClick={() => setAddOpen(true)}>
            Add account
          </button>
        </Can>
      </div>

      {err && <div className="auth-error mt8">{err}</div>}

      <div className="tbl tbl-scroll">
        <div className="tr head" style={{ gridTemplateColumns: COLS }}>
          <div>Account</div>
          <div>Number</div>
          <div>Bank</div>
          <div>Role</div>
          <div>Latest balance</div>
          <div>State</div>
          <div />
        </div>
        {accounts === null &&
          [0, 1, 2].map((i) => (
            <div className="tr body" style={{ gridTemplateColumns: COLS }} key={i}>
              {[0, 1, 2, 3, 4, 5, 6].map((j) => (
                <div key={j} className="skeleton" style={{ width: '60%' }} />
              ))}
            </div>
          ))}
        {accounts?.map((a) => {
          const b = bals[a.number]
          return (
            <div className="tr body" style={{ gridTemplateColumns: COLS }} key={a.id}>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
              <div className="mono" style={{ fontSize: 12 }}>{a.number}</div>
              <div className="muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.bank_name || '—'}</div>
              <div><StatusChip status={titleCase(a.role)} /></div>
              <div className="num">{b ? fmtMoney(b.working_balance, b.currency || a.currency) : '—'}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <StatusChip status={a.active ? 'Active' : 'Suspended'} />
                {a.dispatch_active && <StatusChip status="Dispatch" tone="info" />}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Can perm="manage:config">
                  <button className="btn sm ghost" disabled={busy === a.id} onClick={() => void runBalance(a)}>
                    {busy === a.id ? '…' : 'Balance'}
                  </button>
                  <button className="btn sm ghost" onClick={() => setManage(a)}>
                    Manage
                  </button>
                </Can>
              </div>
            </div>
          )
        })}
        {accounts?.length === 0 && <div className="empty">No accounts configured yet</div>}
      </div>

      {addOpen && <AddAccountDrawer onClose={() => setAddOpen(false)} onDone={() => void load()} />}
      {manage && <ManageAccountDrawer account={manage} onClose={() => setManage(null)} onDone={() => void load()} />}
    </div>
  )
}

// ---- credential fields (shared by add + rotate) -----------------------------

function CredentialFields({ creds, set }: { creds: AccountCredentials; set: (patch: AccountCredentials) => void }) {
  const field = (label: string, k: keyof AccountCredentials, type: 'text' | 'password', hint?: string) => (
    <div className="field">
      <label>{label}</label>
      <input
        className="input"
        type={type}
        autoComplete="new-password"
        aria-label={label}
        value={creds[k] ?? ''}
        onChange={(e) => set({ [k]: e.target.value })}
        placeholder={hint}
      />
    </div>
  )
  return (
    <>
      {field('Consumer key', 'key', 'password')}
      {field('Consumer secret', 'secret', 'password')}
      {field('Passkey (STK)', 'passkey', 'password', 'optional')}
      {field('Initiator name (payouts)', 'initiator', 'text', 'optional')}
      {field('Initiator password (payouts)', 'initiator_password', 'password', 'optional')}
      {field('Security credential', 'security_credential', 'password', 'optional — else generated from the cert')}
    </>
  )
}

// ---- add account ------------------------------------------------------------

function AddAccountDrawer({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('')
  const [number, setNumber] = useState('')
  const [role, setRole] = useState('collection')
  const [creds, setCreds] = useState<AccountCredentials>({})
  const [busy, setBusy] = useState(false)
  const showToast = useUI((s) => s.showToast)

  async function submit() {
    setBusy(true)
    try {
      await createAccount({ name, number, role, ...creds })
      showToast(`Account ${name} added`, 'success')
      onDone()
      onClose()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Could not add account', 'danger')
    } finally {
      setBusy(false)
    }
  }

  const canSubmit = name && number && creds.key && creds.secret && !busy
  return (
    <DrawerShell
      title="Add M-Pesa account"
      onClose={onClose}
      foot={
        <>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!canSubmit} onClick={() => void submit()}>
            {busy ? 'Adding…' : 'Add account'}
          </button>
        </>
      }
    >
      <div className="field">
        <label>Name</label>
        <input className="input" aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nairobi Collections" />
      </div>
      <div className="field">
        <label>Paybill / shortcode</label>
        <input className="input" aria-label="Paybill / shortcode" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="174379" />
      </div>
      <div className="field">
        <label>Role</label>
        <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLES.map((r) => (
            <option key={r} value={r}>{titleCase(r)}</option>
          ))}
        </select>
      </div>
      <div className="card-title mt8">Daraja credentials</div>
      <CredentialFields creds={creds} set={(patch) => setCreds((c) => ({ ...c, ...patch }))} />
      <div className="muted" style={{ fontSize: 12 }}>Stored encrypted; never shown again. Collections need key + secret (+ passkey for STK); payouts also need the initiator + password.</div>
    </DrawerShell>
  )
}

// ---- manage account (activate/deactivate + rotate credentials) --------------

function ManageAccountDrawer({ account, onClose, onDone }: { account: Account; onClose: () => void; onDone: () => void }) {
  const [creds, setCreds] = useState<AccountCredentials>({})
  const [busy, setBusy] = useState(false)
  const showToast = useUI((s) => s.showToast)

  async function toggleActive() {
    setBusy(true)
    try {
      await setAccountActive(account.id, !account.active)
      showToast(`${account.name} ${account.active ? 'deactivated' : 'activated'}`, 'success')
      onDone()
      onClose()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Could not update account', 'danger')
    } finally {
      setBusy(false)
    }
  }

  async function rotate() {
    setBusy(true)
    try {
      await updateAccountCredentials(account.id, creds)
      showToast(`Credentials updated for ${account.name}`, 'success')
      setCreds({})
      onDone()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Could not update credentials', 'danger')
    } finally {
      setBusy(false)
    }
  }

  const anyCred = Object.values(creds).some((v) => v)
  return (
    <DrawerShell
      title={`Manage ${account.name}`}
      onClose={onClose}
      foot={<button className="btn ghost" onClick={onClose}>Close</button>}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 6, fontSize: 13, marginBottom: 12 }}>
        <div className="muted">Number</div><div className="mono">{account.number}</div>
        <div className="muted">Role</div><div>{titleCase(account.role)}</div>
        <div className="muted">State</div><div><StatusChip status={account.active ? 'Active' : 'Suspended'} /></div>
      </div>

      <button className={`btn ${account.active ? 'danger' : 'success'}`} disabled={busy} onClick={() => void toggleActive()}>
        {account.active ? 'Deactivate account' : 'Activate account'}
      </button>

      <div className="card-title mt16">Update credentials</div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Only the fields you fill are changed (the rest are kept).</div>
      <CredentialFields creds={creds} set={(patch) => setCreds((c) => ({ ...c, ...patch }))} />
      <button className="btn primary mt8" disabled={busy || !anyCred} onClick={() => void rotate()}>
        {busy ? 'Saving…' : 'Update credentials'}
      </button>
    </DrawerShell>
  )
}
